import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Card,
    CircularProgress,
    Alert,
    Link,
    useTheme,
    Container,
    Divider,
    Chip
} from '@mui/material';
import {
    MarkEmailRead as MarkEmailReadIcon,
    Refresh as RefreshIcon,
    ArrowBack as ArrowBackIcon,
    CheckCircle as CheckCircleIcon,
    AccessTime as AccessTimeIcon,
    Inbox as InboxIcon
} from '@mui/icons-material';

const RAILWAY_API = 'https://soldikeeper-backend-production.up.railway.app';

const EmailVerificationPending = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();
    const email = location.state?.email || '';
    const name = location.state?.name || '';

    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    const [resendCount, setResendCount] = useState(0);

    const handleResend = async () => {
        if (!email) {
            setError('Email address is missing. Please go back and register again.');
            return;
        }
        setLoading(true);
        setError('');
        setSuccess(false);

        try {
            const res = await fetch(`${RAILWAY_API}/api/auth/resend-verification`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });
            const data = await res.json();
            if (data.success) {
                setSuccess(true);
                setResendCount(c => c + 1);
            } else {
                setError(data.message || 'Failed to resend verification email.');
            }
        } catch {
            setError('Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const isDark = theme.palette.mode === 'dark';

    const steps = [
        { icon: <InboxIcon sx={{ fontSize: 18 }} />, label: 'Open your email inbox' },
        { icon: <MarkEmailReadIcon sx={{ fontSize: 18 }} />, label: 'Find the email from SoldiKeeper' },
        { icon: <CheckCircleIcon sx={{ fontSize: 18 }} />, label: 'Click "Verify my email"' },
    ];

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: isDark
                    ? 'linear-gradient(160deg, #0f172a 0%, #1e293b 100%)'
                    : 'linear-gradient(160deg, #eff6ff 0%, #f0fdf4 100%)',
                p: 2,
            }}
        >
            <Container maxWidth="sm">
                <Card
                    elevation={isDark ? 8 : 4}
                    sx={{
                        borderRadius: 3,
                        overflow: 'visible',
                        position: 'relative',
                        border: isDark
                            ? '1px solid rgba(255,255,255,0.08)'
                            : '1px solid rgba(0,0,0,0.06)',
                    }}
                >
                    {/* Coloured top accent bar */}
                    <Box
                        sx={{
                            height: 5,
                            background: 'linear-gradient(90deg, #2563eb 0%, #7c3aed 100%)',
                            borderRadius: '12px 12px 0 0',
                        }}
                    />

                    <Box sx={{ p: { xs: 3, sm: 5 }, textAlign: 'center' }}>

                        {/* Animated envelope illustration */}
                        <Box
                            sx={{
                                width: 96,
                                height: 96,
                                borderRadius: '50%',
                                background: isDark
                                    ? 'rgba(37,99,235,0.18)'
                                    : 'rgba(37,99,235,0.10)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 3,
                                boxShadow: isDark
                                    ? '0 0 32px rgba(37,99,235,0.25)'
                                    : '0 8px 24px rgba(37,99,235,0.18)',
                            }}
                        >
                            <MarkEmailReadIcon
                                sx={{
                                    fontSize: 52,
                                    color: 'primary.main',
                                }}
                            />
                        </Box>

                        {/* Heading */}
                        <Typography variant="h5" fontWeight={800} gutterBottom sx={{ letterSpacing: '-0.5px' }}>
                            {name ? `You're almost in, ${name.split(' ')[0]}!` : 'Verify your email'}
                        </Typography>

                        {/* Sub-heading */}
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
                            We sent a verification link to:
                        </Typography>

                        {/* Email chip */}
                        <Chip
                            label={email || 'your email address'}
                            icon={<MarkEmailReadIcon />}
                            variant="outlined"
                            color="primary"
                            sx={{
                                mb: 3,
                                fontWeight: 600,
                                fontSize: '0.9rem',
                                px: 1,
                                height: 36,
                                maxWidth: '100%',
                                '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' }
                            }}
                        />

                        {/* Expiry notice */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.75,
                                mb: 3,
                            }}
                        >
                            <AccessTimeIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                            <Typography variant="caption" color="text.secondary">
                                Link expires in <strong>24 hours</strong>
                            </Typography>
                        </Box>

                        {/* Steps */}
                        <Box
                            sx={{
                                bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(37,99,235,0.04)',
                                border: isDark
                                    ? '1px solid rgba(255,255,255,0.07)'
                                    : '1px solid rgba(37,99,235,0.12)',
                                borderRadius: 2,
                                px: 2.5,
                                py: 2,
                                mb: 3,
                                textAlign: 'left',
                            }}
                        >
                            {steps.map((step, i) => (
                                <Box
                                    key={i}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        py: 0.75,
                                        borderBottom: i < steps.length - 1
                                            ? `1px dashed ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`
                                            : 'none',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 28,
                                            height: 28,
                                            borderRadius: '50%',
                                            bgcolor: 'primary.main',
                                            color: 'white',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                            fontSize: 12,
                                            fontWeight: 700,
                                        }}
                                    >
                                        {i + 1}
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                                        {step.icon}
                                        <Typography variant="body2">{step.label}</Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>

                        {/* Alerts */}
                        {success && (
                            <Alert
                                severity="success"
                                icon={<CheckCircleIcon />}
                                sx={{ mb: 2, borderRadius: 2, textAlign: 'left' }}
                            >
                                {resendCount > 1 ? 'Another email sent!' : 'Verification email resent!'} Check your inbox{resendCount > 1 ? ' (and spam)' : ''}.
                            </Alert>
                        )}
                        {error && (
                            <Alert severity="error" sx={{ mb: 2, borderRadius: 2, textAlign: 'left' }}>
                                {error}
                            </Alert>
                        )}

                        {/* Resend button */}
                        <Button
                            variant="contained"
                            fullWidth
                            size="large"
                            onClick={handleResend}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
                            sx={{
                                py: 1.5,
                                borderRadius: 2,
                                fontWeight: 700,
                                fontSize: '0.95rem',
                                textTransform: 'none',
                                mb: 1.5,
                                background: loading
                                    ? undefined
                                    : 'linear-gradient(90deg, #2563eb 0%, #7c3aed 100%)',
                                '&:hover': {
                                    background: 'linear-gradient(90deg, #1d4ed8 0%, #6d28d9 100%)',
                                },
                            }}
                        >
                            {loading ? 'Sending…' : resendCount > 0 ? 'Resend again' : "Resend verification email"}
                        </Button>

                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2.5 }}>
                            Didn't get it? Also check your <strong>spam</strong> or <strong>junk</strong> folder.
                        </Typography>

                        <Divider sx={{ mb: 2.5 }} />

                        {/* Back to login */}
                        <Link
                            component="button"
                            variant="body2"
                            onClick={() => navigate('/login')}
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                color: 'text.secondary',
                                textDecoration: 'none',
                                fontWeight: 500,
                                '&:hover': { color: 'primary.main' },
                            }}
                        >
                            <ArrowBackIcon sx={{ fontSize: 16 }} />
                            Back to Login
                        </Link>
                    </Box>
                </Card>

                {/* Footer note */}
                <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ display: 'block', textAlign: 'center', mt: 3 }}
                >
                    Email sent from{' '}
                    <strong>support@soldikeeper.com</strong>
                </Typography>
            </Container>
        </Box>
    );
};

export default EmailVerificationPending;

